// localDataService.js
// Reads property data from the local properties.json file.
// This is the sole data source for all property reads in the app.

import propertiesData from '../data/properties.json'

// Helper: convert "Society Name" → "society-name" for use in URLs
const toSlug = (str) =>
  str.toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '')

/**
 * Parse a raw properties.json row into the normalised shape used across the app.
 */
const parseProperty = (item) => {
  const photos = item['Photos'] || ''
  const imagesArr = typeof photos === 'string'
    ? photos.split(/[;,]/).map(img => img.trim()).filter(Boolean)
    : (Array.isArray(photos) ? photos : [])

  const facilities = item['Nearby Facilities'] || ''
  const amenitiesArr = typeof facilities === 'string'
    ? facilities.split('•').map(a => a.trim()).filter(Boolean)
    : (Array.isArray(facilities) ? facilities : [])

  const features = item['Additional Features'] || ''
  const additionalFeaturesArr = typeof features === 'string'
    ? features.split('•').map(f => f.trim()).filter(Boolean)
    : (Array.isArray(features) ? features : [])

  return {
    id: item['Property ID'] || item.id,
    title: item['Society Name'] || item.title || '',
    societyName: item['Society Name'] || item.title || '',
    societySlug: toSlug(item['Society Name'] || item.title || 'property'),
    location: item['Location'] || item.location || '',
    flatType: item['Flat Type'] || item.flatType || '',
    roomType: item['Room Type'] || item.roomType || '',
    beds: parseInt(item['Beds']) || 1,
    bedrooms: parseInt(item['Beds']) || 1,
    availability: item['Availability'] || 'Available',
    price: parseFloat(item['Rent (INR/month)']) || 0,
    priceUnit: 'month',
    images: imagesArr,
    amenities: amenitiesArr,
    nearbyFacilities: amenitiesArr,
    additionalFeatures: additionalFeaturesArr,
    description: additionalFeaturesArr.length > 0
      ? additionalFeaturesArr.join(' • ')
      : 'Comfortable accommodation at ' + (item['Society Name'] || 'our society'),
    googleMapsUrl: item['Google Maps URL'] || 'https://maps.google.com/',
    googleRating: item['Google Rating'] || '',
    contactNumber: '9960697118',
    ownerName: 'Chapter 24 Admin',
    email: 'contact@chapter24.com',
    rating: 4.8,
    reviews: 15,
    type: 'Girls',
  }
}

/**
 * Returns all properties (flat list, one entry per row).
 */
export const getPropertiesFromSheet = async () => {
  try {
    const properties = propertiesData.properties.map(parseProperty)
    return { success: true, data: properties }
  } catch (error) {
    console.error('Local data retrieval failed:', error)
    return { success: false, error }
  }
}

/**
 * Groups all rows into Society objects — one card per unique Society Name.
 */
export const getSocietiesFromSheet = async () => {
  const result = await getPropertiesFromSheet()
  if (!result.success) return result

  const societyMap = {}

  result.data.forEach(item => {
    const key = item.societySlug

    if (!societyMap[key]) {
      societyMap[key] = {
        id: key,
        societyName: item.societyName,
        societySlug: key,
        location: item.location,
        googleMapsUrl: item.googleMapsUrl,
        amenities: item.amenities,
        nearbyFacilities: item.nearbyFacilities,
        additionalFeatures: item.additionalFeatures,
        description: item.description,
        googleRating: item.googleRating,
        flatTypes: {}, // flatType → { roomOptions, images, minPrice, maxPrice }
        allImages: [],
        minPrice: Infinity,
        maxPrice: 0,
        displayFlatTypes: [],
      }
    }

    const society = societyMap[key]
    const ft = item.flatType

    if (!society.flatTypes[ft]) {
      society.flatTypes[ft] = {
        images: [],
        roomOptions: [],
        minPrice: Infinity,
        maxPrice: 0,
      }
      society.displayFlatTypes = [...new Set([...society.displayFlatTypes, ft])].sort()
    }

    const ftObj = society.flatTypes[ft]

    // Collect unique images per flat type
    item.images.forEach(img => {
      if (!ftObj.images.includes(img)) ftObj.images.push(img)
      if (!society.allImages.includes(img)) society.allImages.push(img)
    })

    ftObj.roomOptions.push({
      propertyId: item.id,
      roomType: item.roomType,
      beds: item.beds,
      price: item.price,
      availability: item.availability,
      image: item.images[0] || '',
    })

    if (item.price < ftObj.minPrice) ftObj.minPrice = item.price
    if (item.price > ftObj.maxPrice) ftObj.maxPrice = item.price
    if (item.price < society.minPrice) society.minPrice = item.price
    if (item.price > society.maxPrice) society.maxPrice = item.price
  })

  const societies = Object.values(societyMap).map(s => ({
    ...s,
    displayImage: s.allImages[0] || '',
    minPrice: s.minPrice === Infinity ? 0 : s.minPrice,
    roomCount: Object.values(s.flatTypes).reduce((acc, ft) => acc + ft.roomOptions.length, 0),
  }))

  return { success: true, data: societies }
}

export const getSocietyBySlug = async (slug) => {
  const result = await getSocietiesFromSheet()
  if (!result.success) return result
  const society = result.data.find(s => s.societySlug === slug)
  if (society) return { success: true, data: society }
  return { success: false, error: 'Society not found' }
}

export const getPropertyByIdFromSheet = async (propertyId) => {
  try {
    const result = await getPropertiesFromSheet()
    if (result.success && result.data) {
      const property = result.data.find(p => String(p.id) === String(propertyId))
      if (property) {
        return { success: true, data: property }
      }
    }
    return { success: false, error: 'Property not found' }
  } catch (error) {
    console.error('Local property retrieval failed:', error)
    return { success: false, error }
  }
}

